import { get_feature_flag_property } from './feature_flags';

// Feature flag (property "enabled") that switches the redirects on/off per environment.
export const LEGACY_REDIRECT_FEATURE_FLAG = 'legacy-url-redirect';

const LOG_PREFIX = '[legacy-redirect]';

// Legacy hostname -> new portal origin and the base path of its AWS section. Unlisted hosts (e.g. localhost) never redirect.
export const TARGETS = {
  'dev.manage.aws.bmw.cloud': { origin: 'https://dev.portal.bmw.cloud', basePath: '/aws' },
  'int.manage.aws.bmw.cloud': { origin: 'https://int.portal.bmw.cloud', basePath: '/aws' },
  'manage.aws.bmw.cloud': { origin: 'https://portal.bmw.cloud', basePath: '/aws' },
  'int.manage.aws-cn.bmw.cloud': { origin: 'https://int.portal.bmw.cloud', basePath: '/aws-cn' },
  'manage.aws-cn.bmw.cloud': { origin: 'https://portal.bmw.cloud', basePath: '/aws-cn' },
};

// Longer query values are not forwarded
const MAX_PARAM_LENGTH = 512;

// Destination on the new portal for legacy URLs that no rule matches
const FALLBACK_PATH = '/dashboard';

// The jQuery frontend is served from these paths and routes by URL hash.
const LEGACY_ROOT_PATHS = ['/', '/aws/index.html'];

/**
 * Rules are evaluated in order, the first match wins. A URL that matches no rule goes to FALLBACK_PATH.
 *  - `hash`: legacy jQuery page (e.g. /?order_id=...#orderdetails)
 *  - `path`: React micro-frontend (e.g. /idc/list-roles)
 *  - `to`: path below the target's `basePath`
 *  - `query`: explicit allowlist of legacy query params, `{ legacyName: newName }` (same name if unchanged).
 *    Params that are not listed are not forwarded. Values are not validated, only values longer than
 *    MAX_PARAM_LENGTH are dropped.
 */
export const LEGACY_REDIRECT_RULES = [
  { hash: '#dashboard', to: '/dashboard' },
  // Orders
  { hash: '#orders', to: '/orders/list' },
  { hash: '#orderdetails', to: '/orders/details', query: { order_id: 'id' } },

  // Accounts
  { hash: '#accountdetails', to: '/accounts/details', query: { account_id: 'id' } },
  { hash: '#listaccounts', to: '/accounts/list' },
  { hash: '#closeaccount', to: '/accounts/close', query: { account_id: 'id' } },

  // Access management
  { path: '/idc/list-users', to: '/iam/list-users', query: { account_id: 'account_id' } },
  { path: '/idc/list-roles', to: '/iam/list-roles', query: { account_id: 'account_id' } },
  { path: '/idc/order-custom-role', to: '/iam/order-custom-role' },
  { path: '/idc/list-custom-roles', to: '/iam/list-custom-roles' },
  { hash: '#createserviceuser', to: '/iam/create-service-user', query: { account_id: 'account_id' } },
  { hash: '#deleteserviceuser', to: '/iam/delete-service-user', query: { account_id: 'account_id' } },
  { path: '/idc/order-custom-role-account-area', to: '/iam/order-account-area-custom-role' },
  { path: '/idc/list-custom-roles-account-area', to: '/iam/list-account-area-custom-roles' },

  // Networks
  { hash: '#networkdetails', to: '/vpcs/details', query: { vpc_id: 'id' } },
  { hash: '#listvpc', to: '/vpcs/list' },
  { hash: '#createnetwork', to: '/vpcs/update', query: { vpc_id: 'vpc_id', account_id: 'account_id' } },
  { hash: '#destroyvpc', to: '/vpcs/destroy', query: { account_id: 'account_id' } },
  { hash: '#comparevpc', to: '/vpcs/compare', query: { vpc_ids: 'vpc_ids' } },
  { hash: '#createpeering', to: '/vpcs/create-peering' },
  { hash: '#destroypeering', to: '/vpcs/destroy-peering' },

  // FQDN
  { path: '/fqdn/create-fqdn-blocking-report', to: '/fqdn/create-blocking-report' },
  { path: '/fqdn/list-fqdn-blocking-reports', to: '/fqdn/blocking-reports' },
  { path: '/fqdn/request-fqdn-allowlist', to: '/fqdn/request-allowlist' },
  { path: '/fqdn/list-fqdn-allowlist', to: '/fqdn/list-allowlist' },
  { path: '/fqdn/remove-fqdn-allowlist', to: '/fqdn/remove-allowlist' },
  { path: '/fqdn/fqdn-recertification', to: '/fqdn/recertification' },

  // DNS
  { hash: '#hostedzonedetails', to: '/dns/details', query: { domain: 'domain' } },
  { hash: '#listhostedzone', to: '/dns/list' },
  { hash: '#createhostedzone', to: '/dns/create', query: { account_id: 'account_id' } },
  { hash: '#destroyhostedzone', to: '/dns/destroy', query: { account_id: 'account_id', zone: 'zone' } },
  { hash: '#associatehostedzone', to: '/dns/associate', query: { account_id: 'account_id' } },
  { hash: '#onboardhostedzone', to: '/dns/onboard', query: { account_id: 'account_id' } },

  // 4Wheels Guided
  { hash: '#4wheelsdetails', to: '/4wheels/details', query: { cluster_id: 'cluster_id' } },
  { hash: '#list4wheels', to: '/4wheels/list' },

  // Marketplace
  { hash: '#marketplacepurchases', to: '/marketplace', query: { account_id: 'account_id' } },

  // RAI chat
  { hash: '#raichat', to: '/rai-chat' },

  // SIEM onboarding
  { hash: '#siemonboarding', to: '/siem/onboarding' },

  // Trainings
  { hash: '#listTrainingAccounts', to: '/trainings/accounts' },
  { hash: '#listTrainings', to: '/trainings/list' },
  { hash: '#planTraining', to: '/trainings/plan' },

  // Statistics
  { hash: '#networkstatistics', to: '/statistics/networks' },
  { hash: '#accountstatistics', to: '/statistics/accounts' },
  { hash: '#orderstatistics', to: '/statistics/orders' },
  { hash: '#seriesstatistics', to: '/statistics/series' },

  // SCP changelog
  { path: '/scp-changelog/list-scps', to: '/scp-changelog/list-scps' },
  { path: '/scp-changelog/scp-details', to: '/scp-changelog/scp-details' },
  { path: '/scp-changelog/release-details', to: '/scp-changelog/release-details' },
  { path: '/scp-changelog/list-release-schedules', to: '/scp-changelog/list-releases' },

  // Admin
  { hash: '#listsettings', to: '/admin/list-settings' },
  { hash: '#managebanners', to: '/admin/manage-banners' },
  { hash: '#listapikeys', to: '/admin/list-api-keys' },
  { hash: '#featureflags', to: '/admin/feature-flags' },
  { hash: '#listidps', to: '/admin/list-idps' },
  { hash: '#listuserpermissions', to: '/admin/list-user-permissions' },
  { hash: '#adminmoveaccount', to: '/admin/move-account', query: { account_id: 'account_id' } },
  { hash: '#siemcreatekey', to: '/siem/create-key' },
  { hash: '#manage4wheelsclusteroptions', to: '/4wheels/manage-cluster-options' },
  { hash: '#listrightnowportalqueueitems', to: '/admin/right-now-queue' },
  { hash: '#inactivescanner', to: '/admin/inactive-scanner' },

  // User settings
  { hash: '#manageapikeys', to: '/user-settings/api-keys' },
  { hash: '#managem2mportalapiaccess', to: '/user-settings/m2m-access' },

  // General search
  { path: '/search/general-search', to: '/search/general-search', query: { query: 'query' } },
];

function matchesLocation(rule, pathname, hash) {
  if (rule.hash) return LEGACY_ROOT_PATHS.includes(pathname) && hash === rule.hash;
  return pathname.replace(/\/{1,256}$/, '') === rule.path;
}

/**
 * @returns {string|null} absolute URL on the new portal (the dashboard if no rule matches), or null for hosts
 *   without a target
 */
export function resolveLegacyRedirect(
  loc = window.location,
  { targets = TARGETS, rules = LEGACY_REDIRECT_RULES } = {},
) {
  const target = targets[loc.hostname];
  if (!target) return null;

  for (const rule of rules) {
    if (!matchesLocation(rule, loc.pathname, loc.hash)) continue;

    const url = new URL(`${target.origin}${target.basePath}${rule.to}`);
    new URLSearchParams(loc.search).forEach((value, name) => {
      if (!Object.hasOwn(rule.query ?? {}, name)) return;
      if (value.length > MAX_PARAM_LENGTH) {
        console.info(`${LOG_PREFIX} rule ${rule.hash ?? rule.path}: dropping too long param "${name}"`);
        return;
      }
      url.searchParams.append(rule.query[name], value);
    });
    // Commas are valid in a query string; keep list values readable
    return url.toString().replace(/%2C/gi, ',');
  }
  return `${target.origin}${target.basePath}${FALLBACK_PATH}`;
}

/**
 * Redirects to the new portal (the matching page, or its dashboard) if the host has a target and the feature
 * flag is enabled.
 * Needs an authenticated session because feature flags are served by the API.
 * @returns {Promise<boolean>} true if a redirect was triggered
 */
export async function redirectLegacyUrlIfEnabled(
  loc = window.location,
  navigate = url => window.location.replace(url),
) {
  const target = resolveLegacyRedirect(loc);
  if (!target) return false;

  const enabled = await get_feature_flag_property(`${LEGACY_REDIRECT_FEATURE_FLAG}.enabled`, false).catch(() => false);
  if (!enabled) return false;

  console.info(`${LOG_PREFIX} redirecting to ${target}`);
  navigate(target);
  return true;
}
